/* Counting animation for the Stats section. The final value is rendered
   server-side, so the markup is already correct without JS — this only
   animates from 0 up to it once the block scrolls into view. */

class StatsCounter extends HTMLElement {
  connectedCallback() {
    this.numberEl = this.querySelector('[data-stats-number]');
    if (!this.numberEl) return;

    const raw = (this.dataset.value || '').trim();
    this.target = parseFloat(raw.replace(/,/g, ''));
    if (Number.isNaN(this.target)) return;

    const decimalPart = raw.split('.')[1];
    this.decimals = decimalPart ? decimalPart.length : 0;
    this.grouped = raw.includes(',');
    this.duration = parseInt(this.dataset.duration, 10) || 900;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // The zero is written by the first animation frame, not here — if frames
    // never come (backgrounded tab), the server-rendered value stays on screen.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.observer = null;
        this.animate();
      },
      { threshold: 0.25 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  animate() {
    const start = performance.now();

    const step = (now) => {
      const progress = Math.min((now - start) / this.duration, 1);
      // easeOutCubic — quick off the mark, settles on the final value.
      const eased = 1 - Math.pow(1 - progress, 3);
      this.numberEl.textContent = this.format(this.target * eased);

      if (progress < 1) {
        this.frame = requestAnimationFrame(step);
      } else {
        this.frame = null;
        this.numberEl.textContent = this.format(this.target);
      }
    };

    this.frame = requestAnimationFrame(step);
  }

  format(value) {
    const fixed = value.toFixed(this.decimals);
    if (!this.grouped) return fixed;

    const [whole, fraction] = fixed.split('.');
    const withSeparators = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fraction ? `${withSeparators}.${fraction}` : withSeparators;
  }
}

if (!customElements.get('stats-counter')) {
  customElements.define('stats-counter', StatsCounter);
}
